import type { MetrikaGoal } from "./metrika";
import { parseMakeOrderResult } from "./eplex/makeOrderResult";

/**
 * Условия достижения JS-целей Метрики по ответам Eplex API.
 *
 * Чистая функция над ответом: ни одной ссылки на сторы и компоненты. Цели вешаются на
 * ответы, а не на кнопки, потому что одно и то же действие вызывают из разных мест
 * (проценку — из строки поиска, истории и примеров; заказ — из корзины проценки и из
 * журнала), а ручка у действия одна.
 *
 * Все ответы приходят в конверте прокси ЛК `{ status: boolean, error, data }`;
 * полезная нагрузка апстрима лежит в `data`.
 */
export interface TrackedApiResponse {
  method: string;
  url: string;
  status: number;
  data: unknown;
}

interface ProxyEnvelope {
  status?: unknown;
  data?: unknown;
}

interface PricingInfo {
  total?: unknown;
  success?: unknown;
  error?: unknown;
}

const SEARCH_LIVE_PATH = "eplex/search_live";
const SMART_CATALOG_SEARCH_PATH = "eplex/smart-catalog/search";
const TEMP_CART_PATH = "eplex/temp_cart";
const MAKE_ORDER_PATH = "eplex/temp_cart/make_order";
// Только сами результаты: `search/result/{id}/info` (карточка товара) не в счёт
const SEARCH_RESULT_PATH = /^eplex\/search\/result\/([^/?#]+)$/;

/**
 * Путь ручки относительно корня API без query: `/api/eplex/x?y=1` → `eplex/x`.
 * Axios хранит в `config.url` адрес так, как его передали, — и относительным к
 * `baseURL`, и с ведущим слэшем, и с параметрами.
 */
export function normalizeApiPath(url: string): string {
  const withoutQuery = url.split(/[?#]/, 1)[0];
  return withoutQuery.replace(/^\/+/, "").replace(/^api\//, "");
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function isSuccessfulEnvelope(response: TrackedApiResponse): boolean {
  const envelope = asRecord(response.data) as ProxyEnvelope | null;
  return response.status >= 200 && response.status < 300 && envelope?.status !== false;
}

function payloadOf(response: TrackedApiResponse): Record<string, unknown> {
  const envelope = asRecord(response.data) as ProxyEnvelope | null;
  return asRecord(envelope?.data) ?? {};
}

/**
 * Проценка завершена, когда каждый запущенный поставщик ответил — успехом или отказом.
 * Сравнивать `success` с `total` нельзя: почти в каждой проценке часть поставщиков
 * отвечает ошибкой, и такая цель не достигалась бы никогда. Критерий тот же, что у
 * самого ЛК при остановке опроса (`isSearchComplete` в сторе опроса).
 */
function isPricingComplete(payload: Record<string, unknown>): boolean {
  const info = asRecord(payload.info) as PricingInfo | null;
  const total = Number(info?.total);
  const success = Number(info?.success ?? 0);
  const error = Number(info?.error ?? 0);
  return Number.isFinite(total) && total > 0 && success + error >= total;
}

/**
 * Заказ считается оформленным, если бэк принял хотя бы одну позицию: отказ по всей
 * операции (`status: "err"`) или по каждой позиции приходит тем же HTTP 200, и по коду
 * ответа его не отличить (см. `parseMakeOrderResult`).
 */
function isOrderPlaced(response: TrackedApiResponse): boolean {
  const result = parseMakeOrderResult(response.data);
  if (result.error !== null) return false;
  return result.ok || Object.values(result.statuses).some((item) => item.status >= 0);
}

function resolvePostGoal(path: string, response: TrackedApiResponse): MetrikaGoal | null {
  const payload = payloadOf(response);
  if (path === SEARCH_LIVE_PATH && payload.status === "ok") return "pricing_search_started";
  if (path === SMART_CATALOG_SEARCH_PATH && typeof payload.session_id === "string") return "pricing_search_started";
  if (path === MAKE_ORDER_PATH && isOrderPlaced(response)) return "order_placed";
  return null;
}

/**
 * Резолвер целей с памятью о проценках, по которым цель «результаты получены» уже
 * ушла: результаты опрашиваются раз в секунду и после завершения читаются повторно
 * (фильтры, возврат на вкладку), а цель должна уйти один раз на проценку.
 */
export function createMetrikaGoalResolver(): (response: TrackedApiResponse) => MetrikaGoal | null {
  const reportedRequestIds = new Set<string>();

  const resolveResultsGoal = (path: string, response: TrackedApiResponse): MetrikaGoal | null => {
    const requestId = SEARCH_RESULT_PATH.exec(path)?.[1];
    if (!requestId || reportedRequestIds.has(requestId)) return null;
    if (!isPricingComplete(payloadOf(response))) return null;
    reportedRequestIds.add(requestId);
    return "pricing_results_received";
  };

  return (response) => {
    if (!isSuccessfulEnvelope(response)) return null;

    const method = response.method.toUpperCase();
    const path = normalizeApiPath(response.url);

    if (method === "POST") return resolvePostGoal(path, response);
    if (method === "GET") return resolveResultsGoal(path, response);
    if (method === "PUT" && path === TEMP_CART_PATH) return "cart_item_added";
    return null;
  };
}
